import { useSelector } from "react-redux";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreFlightsComponent } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  FilterAirline,
  FilterFareClass,
  FilterStops,
  type FilterListComponentProps,
} from "@tenant/flights";
import { FilterPopover, FilterPrice } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FilterList.module.scss";
import { useFilterList } from "./useFilterList";
import { getPricingMode } from "@/store/flights/shop/store";

export const FilterList = ({
  sliceIndex,
  className,
  children,
}: FilterListComponentProps) => {
  const pricingMode = useSelector(getPricingMode);

  const styles = useFlightStyles(
    CoreFlightsComponent.FilterList,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(styles, CoreFlightsComponent.FilterList, [
    pricingMode,
  ]);

  const { t } = useI18nContext();

  const {
    context: {
      stopsState,
      priceState,
      fareClassesState,
      airlineFilter,

      hadSetStopsFilter,
      hadSetFareClassFilter,
      hadSetAirlineFilter,
      hadSetPriceFilter,
      filterOptions,
    },
    handlers: {
      handleFilterFareClasses,
      handleFilterStopUpdate,
      handleFilterPriceUpdate,
      handleAirlineFilterChange,
      resetFareClassFilter,
      resetStopFilter,
      resetAirlineFilter,
      resetPriceFilter,
    },
  } = useFilterList(sliceIndex);

  return (
    <div className={clsx(block, className)}>
      <FilterPopover
        className={cn("fare-class")}
        label={t("searchFilter.fareClass")}
        isApplied={hadSetFareClassFilter}
        onReset={resetFareClassFilter}
      >
        <FilterFareClass
          fareClasses={fareClassesState}
          onFareClassChange={handleFilterFareClasses}
        />
      </FilterPopover>

      <FilterPopover
        className={cn("stops")}
        label={t("searchFilter.stops")}
        isApplied={hadSetStopsFilter}
        onReset={resetStopFilter}
      >
        <FilterStops
          value={stopsState}
          onStopFilterChange={(val) => {
            handleFilterStopUpdate(val);
          }}
        />
      </FilterPopover>

      <FilterPopover
        className={cn("airline")}
        label={t("searchFilter.airline")}
        isApplied={hadSetAirlineFilter}
        onReset={resetAirlineFilter}
      >
        <FilterAirline
          allAirlines={filterOptions.airlineOptions}
          airlineFilter={airlineFilter}
          onAirlineFilterChange={handleAirlineFilterChange}
        />
      </FilterPopover>

      <FilterPopover
        className={cn("price")}
        label={t("searchFilter.price")}
        isApplied={hadSetPriceFilter}
        onReset={resetPriceFilter}
      >
        <FilterPrice
          className={cn("filter-popover")}
          label={t("searchFilter.maxPrice")}
          minimumPrice={filterOptions.priceMin}
          maximumPrice={filterOptions.priceMax}
          initialValue={{
            minPrice: filterOptions.priceMin?.value || 0,
            maxPrice: priceState,
          }}
          onFilterPriceChange={handleFilterPriceUpdate}
        />
      </FilterPopover>

      {children}
    </div>
  );
};
